import { type SelectProps } from '@mantine/core';
import { camelCaseToLabel } from '../../formatters/camelCaseFormatter';
import { EnumSelector, type EnumSelectorProps } from '../../inputs/EnumSelector';
import { useEditorContext } from '../EditorContext';
import { setEditorFieldValue, type LooseKeys } from '../types';
import { ModalEditorItemBase } from './ModalEditorItemBase';

export interface ModalEnumItemProps<TFormType, T extends Record<string, string | number>>
    extends Omit<EnumSelectorProps<T>, 'value' | 'onChange'>,
        Omit<SelectProps, 'value' | 'onChange' | 'data'> {
    fieldKey: LooseKeys<TFormType>;
    required?: boolean;
    label?: string;
    labelWidth?: number;
}

export const ModalEnumItem = <TFormType, T extends Record<string, string | number>>({
    label,
    labelWidth,
    fieldKey,
    enumObject,
    labelFormatter,
    required,
    ...selectProps
}: ModalEnumItemProps<TFormType, T>) => {
    const { form, loading, onSave } = useEditorContext<TFormType>();

    const fieldKeyString = fieldKey as string;
    const error = form.errors[fieldKeyString];

    const handleChange = (value: string | null) => {
        setEditorFieldValue(form, fieldKeyString, value);
    };

    const value = form.getInputProps(fieldKeyString).value;

    return (
        <ModalEditorItemBase
            label={label}
            labelWidth={labelWidth}
            error={error}
            required={required}
            submitChanges={onSave}
        >
            <EnumSelector
                key={form.key(fieldKeyString)}
                enumObject={enumObject}
                labelFormatter={labelFormatter ?? camelCaseToLabel}
                {...form.getInputProps(fieldKeyString)}
                value={value}
                onChange={handleChange}
                disabled={loading}
                error={false}
                {...selectProps}
            />
        </ModalEditorItemBase>
    );
};
